import React, { useState } from "react"
import { VictoryPie, VictoryTooltip } from "victory"
import { Label } from "../Form"
import { Column, Row } from "../Layout"
import PivotTableUI from "react-pivottable/PivotTableUI"
import "react-pivottable/pivottable.css"
import TableRenderers from "react-pivottable/TableRenderers"
import Plot from "react-plotly.js"
import createPlotlyRenderers from "react-pivottable/PlotlyRenderers"

export const PlotBR = (props) => {
  return <Plot {...props} config={{ ...props.config, locale: "pt-br", displaylogo: false }} />
}

// create Plotly renderers via dependency injection
const PlotlyRenderers = createPlotlyRenderers(PlotBR)

export const DashboardComponent = ({ type, label, data }) => {
  if (!["radio", "checkbox", "select"].includes(type)) {
    return <></>
  }
  return (
    <>
      <Label label={label} />
      {["radio", "checkbox", "select"].includes(type) && <DashboardPie data={data} />}
    </>
  )
}

const formatPie = (data) => {
  const formattedData = data
    .map((item) => {
      return `${item.Resposta}`.split("|").map((Splitted) => ({ ...item, Splitted }))
    })
    .flat()
  const answers = [...new Set(formattedData.map((item) => item.Splitted))]
  const total = formattedData?.length

  if (!total) {
    return []
  }

  return answers.map((value) => ({
    x: value,
    y: ((formattedData.filter((item) => item.Splitted === value).length / total) * 100).toFixed(2),
    z: formattedData.filter((item) => item.Splitted === value).length,
  }))
}

const formatLabel = (item) => `${item.z} - ${item.x} (${item.y}%)`

export const DashboardPie = ({ data }) => {
  const formattedData = formatPie(data)
  return (
    <Row>
      <Column cols={6}>
        <VictoryPie
          data={formattedData}
          colorScale={["tomato", "orange", "gold", "cyan", "navy"]}
          labelComponent={<VictoryTooltip />}
          labels={({ datum }) => formatLabel(datum)}
        />
      </Column>
      <Column cols={6}>
        {formattedData.map((item) => {
          return <Label label={formatLabel(item)} />
        })}
      </Column>
    </Row>
  )
}

const data = [
  ["attribute", "attribute2"],
  ["value1", "value2"],
  ["value3", "value4"],
]

const formatData = (data, dw) => {
  if (!data || !data[0] || !data.length) {
    return [];
  }

  const array = [];

  const isDateLike = (value) => {
    if (typeof value !== 'string') return false;
    // Aceita formatos como YYYY-MM-DD, YYYY/MM/DD, ou datas ISO
    return /^\d{4}[-/]\d{2}[-/]\d{2}$/.test(value);
  };

  const toBrazilianDate = (value) => {
    if (!isDateLike(value)) return null;

    const date = new Date(value);
    if (!isNaN(date)) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return null;
  };

  const formatResposta = (value) => {
    if (value === 'Ø') return '';
    if (value === true || value === 'true') return 'Sim';
    if (value === false || value === 'false') return 'Não';

    const asDate = toBrazilianDate(value);
    if (asDate) return asDate;

    return value;
  };

  const additionalData = data.map((item) => {
    const rawResposta = item.Resposta ?? item[item.Pergunta_codigo || item.Pergunta_codigo_1];

    const Mes = item.Mes || String(new Date(item.realInicio).getMonth() + 1).padStart(2, '0')
    const Ano = item.Ano || new Date(item.realInicio).getFullYear()

    return {
      Etapa: item.Etapa,
      Etapa_Pergunta: item.Etapa_Pergunta,
      Pergunta_codigo: item.Pergunta_codigo || item.Pergunta_codigo_1,
      Etapa_Pergunta_codigo: item.Etapa_Pergunta_codigo,
      Etapa_codigo: item.Etapa_codigo,
      Pergunta: item.Pergunta,
      Mes: Mes,
      Ano: Ano,
      Resposta: formatResposta(rawResposta),
      Produtor:
        dw?.farmers?.find((row) => row.value === item.Produtor || row?.value === item.ProdutorId)
          ?.label,
      Formulario: dw?.forms?.find((row) => row.value === item.Formulario)?.label,
      Projeto:
        dw?.projects?.find((row) => row.value === item.Projeto)?.label || item.Projeto,
    };
  });

  const keys = Object.keys(additionalData[0]);
  array.push(keys);

  additionalData.forEach((item) => {
    const itemData = keys.map((key) => item[key]);
    array.push(itemData);
  });

  return array;
};

const defaultData = {
  cols: ["Produtor"],
  rows: ["Formulario", "Pergunta"],
  vals: ["Resposta"],
  aggregatorName: "Last",
  rendererName: "Table Row Heatmap",
}

export const PivotTable = ({ data, dw }) => {
  //data
  const [state, setState] = useState(defaultData)

  return (
    <PivotTableUI
      data={formatData(data, dw)}
      onChange={(s) => setState(s)}
      renderers={Object.assign({}, TableRenderers, PlotlyRenderers)}
      {...state}
    />
  )
}
